import React, {useEffect, useState} from 'react';
import './BookingOverlay.css'
import '../../Styles.css'
import {
  GetBookingInfoRequest,
  GetBookingInfoResponse,
  IApiBusinessProfileProto,
  IClientRelationshipDetailsProto,
  IPackageProto,
  IPriceCategoryProto,
  IPublicBusinessInfoProto,
  IPurchasedPackageProto, IPurchasedSingleClassCreditProto,
  IScheduledClassProto,
  space
} from "../../compiled";
import {RpcState} from "../../util/RpcState";
import {useRpcSender} from "../../RpcSender";
import {useSearchParams} from "react-router-dom";
import {BookingFlowPage} from "./BookingFlowPage";
import {AppointmentBookingFlow} from "./Appointment/AppointmentBookingFlow";
import {ChooseBookingType} from "./ChooseBookingType";
import {ClassBookingFlow} from "./Class/ClassBookingFlow";
import {App2} from "../App2";
import {useAuth0} from "@auth0/auth0-react";
import {BookingFlowLogin} from "./BookingFlowLogin";
import {NewClientInquiry} from "./NewClientInquiry";
import {BookingLeftNav} from "./BookingLeftNav";
import {useProfile} from "../../UserProfileProvider";
import {ConfirmBooking} from "./ConfirmBooking";
import {SelectPurchaseOption} from "./SelectPurchaseOption";
import {isContactEmailVerified, isMobileNumberVerified} from "../../util/ContactUtil";
import {CreateUserAccount} from "./CreateUserAccount";
import {MobileNumberVerification} from "./MobileNumberVerification";
import {EmailVerification} from "./EmailVerification";
import {LoadingDots} from "../../util/UI/LoadingDots";
import IBusinessLocationProto = space.kenko.proto.IBusinessLocationProto;
import IServiceProto = space.kenko.proto.IServiceProto;
import IPractitionerProto = space.kenko.proto.IPractitionerProto;
import IPriceProto = space.kenko.proto.IPriceProto;
import IClassTemplateProto = space.kenko.proto.IClassTemplateProto;

/**
 * Booking flow for appointments. This uses URL params for the various fields to
 * make the navigation browser-friendly and intuitive. It also allows linking to a
 * provider or location from a provider website.
 */
export const BookingFlow = () => {

  const [searchParams, setSearchParams] = useSearchParams();
  const rpcSender = useRpcSender()

  // All information is selected as a search param. This makes browser navigation work
  // and it makes all these pages directly linkable.
  const businessId = searchParams.get('b') as string;
  const version = searchParams.get('v') ?? undefined; // this is for testing

  // This is a marker that a booking has been selected. It's basically here
  // just to allow navigation
  const selectedBookingId = searchParams.get('sb') ?? undefined;
  const selectedPurchaseTypeId = searchParams.get('pt') ?? undefined;


  const forceInquiry = "true" === searchParams.get('forceInquiry'); // for testing in dev

  // "class" or "appt"
  const bookingKind = searchParams.get('k') as string;

  const [rpcState, setRpcState] = useState<RpcState>({state: 'none'})

  const [business, setBusiness] = useState<IPublicBusinessInfoProto | null>();
  const [businessProfile, setBusinessProfile] = useState<IApiBusinessProfileProto | null>();
  const [clientRelationship, setClientRelationship] = useState<IClientRelationshipDetailsProto | null>();

  const [selectedPurchaseType, setSelectedPurchaseType] = useState<PurchaseOption>();

  // This is the details about either the appointment or the class they chose
  const [selectedBooking, setSelectedBooking] = useState<SelectedBooking>();

  const {user, isLoading} = useAuth0();
  const {profile, profileLoading} = useProfile();

  useEffect(() => {
    // This happens if the user hits the back button and wipes out the 'sb'
    // parameter. We don't want them to be able to access the possibly-stale
    // booking details. 
    if (!selectedBookingId && selectedBooking) {
      setSelectedBooking(undefined);
    }

    if (!selectedPurchaseTypeId && selectedPurchaseType) {
      setSelectedPurchaseType(undefined);
    }
  }, [selectedBookingId, selectedPurchaseTypeId]);

  /**
   * This adds a url parameter called "sb" to the URL and saves the selected booking
   * to the react app state. 
   *
   * The reason for this is basically to make the back button work; we want
   * some URL parameter to be appended and removed to enable forward and
   * back to work properly.
   *
   * Second, it's a pain to serialize the SelectedBooking object into a query
   * parameter. So we just save it locally and reference it by ID. Only a single
   * one is stored at a time to make sure stale ones are not tolerated.
   */
  const saveBookingAndSessionId = (booking: SelectedBooking) => {
    console.log('saveBookingAndSessionId', booking);

    setSelectedBooking(booking);

    // Postpaid appointments (reservation only) don't require the user to select
    // a purchase type so we set the 'pt' here to skip it. For prepaid appointments
    // the user has to select a credit or package.
    if (booking.appointment && booking.appointment.service.paymentPolicy?.postpaid) {
      updateParams([
        {k: 'sb', v: '1'},
        {k: 'pt', v: '1'},
      ]);

      setSelectedPurchaseType({
        postPaid: true
      })
    } else {

      updateParams([
        {k: 'sb', v: '1'},
        {k: 'pt', v: undefined},
      ]);

      setSelectedPurchaseType(undefined);
    }
  }

  const savePurchasedTypeAndId = (purchaseOption: PurchaseOption) => {
    console.log('savePurchaseTypeAndId', purchaseOption);
    updateParams([{k: 'pt', v: '1'}]);
    setSelectedPurchaseType(purchaseOption);
  }

  useEffect(() => {

    if (isLoading) {
      // Wait to see if there's a user before sending the rpc
      return;
    }
    
    setRpcState({state: 'pending'});
    rpcSender.send('get_booking_info',
        GetBookingInfoRequest.encode(new GetBookingInfoRequest({
          businessId: businessId,
          version: version,
        })).finish())
        .then(responseBytes => {
          let response = GetBookingInfoResponse.decode(responseBytes);
          
          if (!response.okay || !response.business) {
            setRpcState({state: 'error', message: 'Error loading this provider'});
            return;
          }
          setRpcState({state: 'success'});
          setBusinessProfile(response.businessProfile);
          setClientRelationship(response.clientRelationship);
          setBusiness(response.business);
        })
        .catch(e => {
          console.warn('Error loading business', e);
          setRpcState({state: 'error', message: e.toString()});
        })
    
    // This is predicated upon the user because the RPC is technically unauthenticated
    // but might need to reload once the user has been loaded. 
  }, [user, isLoading])

  /**
   * IMPORTANT -- don't call this multiple times per frame / render.
   * This will cause multiple immediate navigations which results in dropped
   * parameters and weird navigation.
   */
  const updateParams = (updated: { k: string, v: string | null | undefined } []) => {
    setSearchParams(existing => {
      const newSearchParams = new URLSearchParams(existing);
      updated.forEach(kv => {
        if (kv.v) {
          newSearchParams.set(kv.k, kv.v);
        } else {
          newSearchParams.delete(kv.k);
        }
      });
      return newSearchParams;
    });
  }


  const getContent = () => {

    if (isLoading || rpcState.state == "pending") {
      return <div className={'BookingOverlayLoading'}>Loading<LoadingDots/></div>
    }
    
    if (rpcState.state == "error") {
      return <BookingFlowPage
          title={`Page not found`}
          content={<div className={'BookingOverlayBottomRow '}>
            Please check that you have the right link</div>}
      />
    }

    if (!businessProfile) {
      return <></>;
    }

    if (business?.newClientsMustInquire && !user) {
      // This is an earlier login gate for businesses where new clients need to inquire;
      // we don't want you to be able to see all the classes and appointments only to
      // find out that you aren't allowed to book.
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<BookingFlowLogin/>}
      />;
    }
    
    // You also need a user entity if you're going to inquire about a business
    // so push this gate up as well
    if (business?.newClientsMustInquire && !profile) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<CreateUserAccount/>}
      />;
    }

    if (forceInquiry || (business?.newClientsMustInquire && !clientRelationship)) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<NewClientInquiry businessId={businessId}/>}
      />;
    }

    // If a booking kind is not selected

    const showAppointments = (bookingKind == 'appt' || (business?.offersAppointments && !business?.offersClasses));
    const showClasses = (bookingKind == 'class' || (business?.offersClasses && !business.offersAppointments));

    // If the provider offers both classes and appointments, require this step to choose which 
    // they want to book.

    if (!showAppointments && !showClasses) {
      return <BookingFlowPage
          title={"Book with " + businessProfile.proto?.businessName}
          content={<ChooseBookingType business={businessProfile}
                                      onSelect={v => updateParams([{k: 'k', v: v}])}/>}
      />;
    }

    if (!selectedBookingId || !selectedBooking) {
      if (showAppointments) {
        return <AppointmentBookingFlow business={businessProfile}
                                       onChosen={saveBookingAndSessionId}/>
      }

      if (showClasses) {
        return <ClassBookingFlow businessProfile={businessProfile}
                                 onChosen={saveBookingAndSessionId}/>
      }
    }

    // There's a selected booking at this step. Now we require a login gate.

    // Here lies the login gate. If there's no profile / user it must be created
    // for sure definite.
    if (!user) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          leftNav={<BookingLeftNav
              businessProfile={businessProfile}
              selectedBooking={selectedBooking as SelectedBooking}/>}
          content={<BookingFlowLogin/>}
      />;
    }

    if (profileLoading) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          leftNav={<BookingLeftNav
              businessProfile={businessProfile}
              selectedBooking={selectedBooking as SelectedBooking}/>
          }
          content={<div style={{padding: 20}}>Loading profile...</div>}
      />;
    }


    // Booking requires all the following:
    // 1. An user account with name, profile, and accepted terms.
    // 2. A verified email
    // 3. A verified phone number.
    if (!profile) {

      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<CreateUserAccount/>}
      />;
    }

    if (!isContactEmailVerified(profile)) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<EmailVerification/>}
      />;
    }

    if (!isMobileNumberVerified(profile)) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          content={<MobileNumberVerification/>}
      />; 
    }
    
    // This is only shown for prepaid bookings.
    if (!selectedPurchaseTypeId || !selectedPurchaseType) {
      return <BookingFlowPage
          title={`Book with ${businessProfile?.proto?.businessName}`}
          leftNav={<BookingLeftNav
              businessProfile={businessProfile}
              selectedBooking={selectedBooking as SelectedBooking}/>}
          content={<SelectPurchaseOption business={businessProfile}
                                         clientRelationship={clientRelationship}
                                         selectedBooking={selectedBooking as SelectedBooking}
                                         onSelected={savePurchasedTypeAndId}/>}
      />
    }

    // Everything's ready. Now we show a confirmation page. Once the user hits confirm, they're redirected
    // either to stripe or to the booking confirmation page. This is the last step since it ends in a
    // redirect
    return <BookingFlowPage
        title={`Book with ${businessProfile?.proto?.businessName}`}
        leftNav={<BookingLeftNav
            businessProfile={businessProfile}
            selectedBooking={selectedBooking as SelectedBooking}
            purchaseOption={selectedPurchaseType as PurchaseOption}
        />}
        content={<ConfirmBooking
            isPreview={version === 'DRAFT'}
            businessId={businessProfile.businessId as string}
            selectedBooking={selectedBooking as SelectedBooking}
            purchaseOption={selectedPurchaseType as PurchaseOption}
        />}
    />;
  }

  return <App2>{getContent()}</App2>
}

/**
 * A kinda generic way to represent whether it was a class or appointment that was chosen.
 * This helps unify the confirmation and checkout pages so the whole flow isn't duplicated.
 */
export type SelectedBooking = {
  class?: IScheduledClassProto,
  appointment?: {
    location: IBusinessLocationProto | null | undefined,
    virtualAppt: boolean | null | undefined,
    service: IServiceProto,
    practitioner: IPractitionerProto,
    timestamp: number,
  }
}

export type PurchaseOption = {
  buySingleCredit?: {
    priceCategory?: IPriceCategoryProto | null | undefined,
    class?: IClassTemplateProto | null | undefined,
    service?: IServiceProto | null | undefined,
  }
  buyNewClassPackage?: IPackageProto | null,
  useExistingClassPackage?: IPurchasedPackageProto | null,
  useExistingClassCredit?: IPurchasedSingleClassCreditProto | null,
  postPaid?: boolean,
}